import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageX, AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

/**
 * Compact "Expiry at my store" widget for staff + SM dashboards.
 * Pulls /expiry-tracker/store-list and shows the 5 nearest-expiry pending
 * items. Click row → /expiry-list (full view).
 */
export default function MyStoreExpiryWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiRequest('/expiry-tracker/store-list');
      // Endpoint may return either {items:[...]} or [...] depending on version
      const list = Array.isArray(d) ? d : (d.items || []);
      const pending = list.filter(x => !x.action_taken);
      pending.sort((a, b) => (a.expiry_date || '').localeCompare(b.expiry_date || ''));
      setItems(pending.slice(0, 5));
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(i);
  }, [load]);

  const daysUntil = (d) => {
    if (!d) return null;
    const days = Math.ceil((new Date(d) - new Date()) / 86400000);
    return days;
  };

  return (
    <Card data-testid="my-store-expiry-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <PackageX className="h-4 w-4 text-amber-600" />
          Expiry at My Store
          {items.length > 0 && (
            <Badge variant="destructive" className="ml-auto h-5 px-1.5 text-xs">
              {items.length} pending
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">No near-expiry items pending action 🎉</p>
        ) : (
          <div className="space-y-1.5" data-testid="my-store-expiry-list">
            {items.map((item, idx) => {
              const d = daysUntil(item.expiry_date);
              const tone = d <= 0
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : d <= 3
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700';
              return (
                <div
                  key={item.id || idx}
                  className={`flex items-center justify-between border rounded-md px-2.5 py-1.5 ${tone}`}
                  data-testid={`expiry-row-${idx}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{item.product_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      qty {item.quantity} · exp {item.expiry_date}
                    </div>
                  </div>
                  <div className="shrink-0 ml-2 flex items-center gap-1">
                    {d <= 0 ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    ) : null}
                    <span className={`text-xs font-semibold tabular-nums ${
                      d <= 0 ? 'text-red-600' : d <= 3 ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'
                    }`}>
                      {d <= 0 ? 'expired' : `${d}d`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Button
          variant="ghost" size="sm"
          className="w-full justify-between mt-2 text-amber-700 dark:text-amber-300"
          onClick={() => navigate('/expiry-list')}
          data-testid="my-store-expiry-open-btn"
        >
          Open full list
          <ChevronRight className="h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}
